import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { ProcessedTrucksListWrapper } from "./ProcessedTrucksList.styles";
import ProcessedTrucksCard from "components/Trucks/components/ProcessedTrucksCard/ProcessedTrucksCard";
import { useGetTrucksQuery } from "features/api/truckApi";
import NoTrucksMessage from "../components/NoTrucksMessage/NoTrucksMessage";
import { models } from "types/api/viewModels";
import ProcessedTrucksListSkeleton from "../../Skeletons/ProcessedTrucksListSkeleton";
import ProcessedTrucksFilters from "../components/ProcessedTruckListFilters/ProcessedTrucksListFilters";
import {
	CompleteTrucksStatusLabel,
	InProductionTrucksStatusLabel,
	OpenTrucksStatusLabel,
	TruckPendingText
} from "constants/text";
import { TruckStatus } from "types/declarations/enums.ts";
import TruckDetailsFlyout from "../TruckDetailsFlyout/TruckDetailsFlyout";

const ProcessedTrucksList: FC = () => {
	const { data: trucks, isLoading } = useGetTrucksQuery();
	const [statusFilter, setStatusFilter] = useState<string[]>([]);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [statuses, setStatuses] = useState<{ value: string; label: string }[]>([]);
	const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
	const [selectedTruck, setSelectedTruck] = useState<models["TruckViewModel"] | null>(null);

	const initialFilteredTrucks = useMemo(() => {
		return (trucks ?? []).filter((truck: models["TruckViewModel"]) => {
			const isDeliveryDateValid =
				new Date(truck.requestedDeliveryDate) >= new Date(new Date().setMonth(new Date().getMonth() - 6));
			const isStatusValid =
				truck.truckStatus && [TruckStatus.IN_PRODUCTION, TruckStatus.COMPLETE].includes(truck.truckStatus);
			return isDeliveryDateValid && isStatusValid;
		});
	}, [trucks]);

	const sortAndFilterTrucks = useCallback(
		(trucks: models["TruckViewModel"][] | undefined) => {
			return trucks?.filter((truck: models["TruckViewModel"]) => {
				const truckStatus = truck.truckStatus ?? "";
				const isStatusFiltered = statusFilter.length === 0 || statusFilter.includes(truckStatus);
				const isSearchMatch = [truck.truckNumber ? truck.truckNumber : "#" + TruckPendingText, truck.truckName]
					.filter(Boolean)
					.join(" - ")
					.toLowerCase()
					.includes(searchTerm.toLowerCase());

				return isStatusFiltered && isSearchMatch;
			});
		},
		[searchTerm, statusFilter]
	);

	const filteredTrucks = sortAndFilterTrucks(initialFilteredTrucks);

	useEffect(() => {
		const statusLabels = {
			IN_PRODUCTION: InProductionTrucksStatusLabel,
			COMPLETE: CompleteTrucksStatusLabel,
			OPEN: OpenTrucksStatusLabel
		};

		const uniqueStatuses: { value: string; label: string }[] = Array.from(
			new Set(initialFilteredTrucks.map((truck: models["TruckViewModel"]) => truck.truckStatus))
		).map((status) => ({
			value: status ?? "UNKNOWN",
			label: status ? statusLabels[status] || status.replace(/_/g, " ") : "Unknown"
		}));

		setStatuses(uniqueStatuses);
	}, [initialFilteredTrucks]);

	if (isLoading) return <ProcessedTrucksListSkeleton />;

	const searchOptions: string[] =
		filteredTrucks?.map(
			(truck: models["TruckViewModel"]) => `${truck.truckNumber ?? "#" + TruckPendingText} - ${truck.truckName}`
		) || [];

	const handleFlyoutOpen = (truck: models["TruckViewModel"]) => {
		setSelectedTruck(truck);
		setIsFlyoutOpen(true);
	};

	const handleFlyoutClose = () => {
		setIsFlyoutOpen(false);
		setSelectedTruck(null);
	};

	return (
		<>
			<ProcessedTrucksListWrapper>
				<ProcessedTrucksFilters
					statusFilter={statusFilter}
					setStatusFilter={setStatusFilter}
					resultsCount={filteredTrucks ? filteredTrucks.length : 0}
					searchTerm={searchTerm}
					setSearchTerm={setSearchTerm}
					searchOptions={searchOptions}
					statuses={statuses}
					filteredTrucks={filteredTrucks}
				/>
				{!filteredTrucks || filteredTrucks.length === 0 ? (
					<NoTrucksMessage />
				) : (
					filteredTrucks.map((truck) => (
						<ProcessedTrucksCard
							key={truck.truckId}
							truck={truck}
							onFlyoutOpen={() => handleFlyoutOpen(truck)}
						/>
					))
				)}
			</ProcessedTrucksListWrapper>
			<TruckDetailsFlyout
				isOpen={isFlyoutOpen}
				onClose={handleFlyoutClose}
				truck={selectedTruck}
			/>
		</>
	);
};

export default ProcessedTrucksList;
